import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { InfoTip } from "@/components/app/info-tip";
import { PlacementStatusLegend } from "@/components/app/placement-status-legend";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Flame, Inbox, AlertTriangle, Tag } from "lucide-react";

export const Route = createFileRoute("/_authenticated/placement-heatmap")({
  component: PlacementHeatmapPage,
  head: () => ({ meta: [{ title: "Placement-Heatmap - InboxPulse" }] }),
});

const WINDOWS: Record<string, number> = {
  "1h": 3_600_000,
  "24h": 24 * 3_600_000,
  "7d": 7 * 24 * 3_600_000,
};

type Row = {
  id: string;
  provider: string | null;
  sending_domain: string | null;
  placement: string;
  campaign_id: string | null;
  created_at: string;
};

type Cell = {
  key: string;
  label: string;
  total: number;
  inbox: number;
  spam: number;
  promotions: number;
  other: number;
  inboxRate: number;
  spamRate: number;
};

function countSignalStatuses(rows: Row[]) {
  const counts = { promotions: 0, spam: 0, blocked: 0, delayed: 0 };
  for (const row of rows) {
    const placement = (row.placement || "").toLowerCase();
    if (placement === "promotions") counts.promotions++;
    else if (placement === "spam") counts.spam++;
    else if (["blocked", "rejected", "bounced"].includes(placement)) counts.blocked++;
    else if (["delayed", "deferred", "pending"].includes(placement)) counts.delayed++;
  }
  return counts;
}

function aggregate(rows: Row[], dim: "provider" | "sending_domain"): Cell[] {
  const m = new Map<string, Cell>();
  for (const r of rows) {
    const label = (r[dim] ?? "unknown") || "unknown";
    let c = m.get(label);
    if (!c) {
      c = {
        key: label,
        label,
        total: 0,
        inbox: 0,
        spam: 0,
        promotions: 0,
        other: 0,
        inboxRate: 0,
        spamRate: 0,
      };
      m.set(label, c);
    }
    c.total++;
    const p = (r.placement || "").toLowerCase();
    if (p === "inbox") c.inbox++;
    else if (p === "spam") c.spam++;
    else if (p === "promotions") c.promotions++;
    else c.other++;
  }
  for (const c of m.values()) {
    c.inboxRate = c.total ? c.inbox / c.total : 0;
    c.spamRate = c.total ? (c.spam + c.promotions) / c.total : 0;
  }
  return [...m.values()].sort((a, b) => b.total - a.total);
}

function rateColor(inboxRate: number, total: number): string {
  // Niedrige Sample-Counts werden gedämpft dargestellt
  const alpha = Math.min(1, 0.25 + total / 50);
  // grün bei hohem Inbox-Rate, rot bei niedrig
  const hue = Math.round(inboxRate * 140); // 0=rot, 140=grün
  return `oklch(0.55 0.18 ${hue} / ${alpha})`;
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function PlacementHeatmapPage() {
  const [windowKey, setWindowKey] = useState<keyof typeof WINDOWS>("24h");
  const [drill, setDrill] = useState<{ dim: "provider" | "sending_domain"; label: string } | null>(
    null,
  );

  const sinceIso = useMemo(
    () => new Date(Date.now() - WINDOWS[windowKey]).toISOString(),
    [windowKey],
  );

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["placement-heatmap", windowKey],
    queryFn: async () => {
      const [{ data: results }, { data: campaigns }] = await Promise.all([
        supabase
          .from("placement_results")
          .select("id,provider,sending_domain,placement,campaign_id,created_at")
          .gte("created_at", sinceIso)
          .order("created_at", { ascending: false })
          .limit(5000),
        supabase.from("campaigns").select("id,name"),
      ]);
      const campaignMap = new Map<string, string>();
      for (const c of campaigns ?? []) campaignMap.set(c.id, c.name);
      return { rows: (results ?? []) as Row[], campaignMap };
    },
    refetchInterval: 15_000,
  });

  // Realtime: refetch bei neuem Ergebnis
  useEffect(() => {
    const ch = supabase
      .channel("placement-heatmap")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "placement_results" },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [refetch]);

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const providerCells = useMemo(() => aggregate(rows, "provider"), [rows]);
  const domainCells = useMemo(() => aggregate(rows, "sending_domain"), [rows]);
  const signalCounts = useMemo(() => countSignalStatuses(rows), [rows]);

  const totals = useMemo(() => {
    const t = { total: rows.length, inbox: 0, spam: 0, promotions: 0 };
    for (const r of rows) {
      const p = (r.placement || "").toLowerCase();
      if (p === "inbox") t.inbox++;
      else if (p === "spam") t.spam++;
      else if (p === "promotions") t.promotions++;
    }
    return t;
  }, [rows]);

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Placement-Heatmap"
        description="Inbox-Rate vs. Spam-Rate pro Provider und Sender-Domain - live aus Seed-Inbox-Scans."
      />

      <HelpPanel
        what={
          <>
            Die <strong>Zustell-Heatmap</strong> zeigt dir auf einen Blick, wo deine E-Mails
            tatsächlich landen: im <strong>Posteingang</strong>, im <strong>Spam</strong>-Ordner
            oder im Tab <strong>Promotions</strong> (Werbung). Die Daten kommen live aus deinen
            Seed-Postfächern - das sind echte Test-Mailaccounts bei Anbietern wie Gmail, Outlook
            usw., an die du deine Mailings automatisch mitschickst.
          </>
        }
        why={
          <>
            Wenn deine Mails im Spam landen, sehen sie deine Empfänger nie. Diese Seite hilft dir zu
            erkennen, <strong>bei welchem E-Mail-Anbieter</strong> oder{" "}
            <strong>von welcher Absender-Domain</strong> die Zustellung gerade schlecht ist - damit
            du eingreifen kannst, bevor eine echte Kampagne darunter leidet.
          </>
        }
        how={[
          <>
            Wähle oben links ein <strong>Zeitfenster</strong> (z. B. "Letzte 24 h"). Die Kacheln
            werden für genau diesen Zeitraum berechnet.
          </>,
          <>
            Schau dir die <strong>Farben</strong> an: Grün = viele Mails landen im Posteingang, Rot
            = viele landen im Spam/Promotions. Je dunkler die Farbe, desto verlässlicher der Wert
            (mehr Stichproben).
          </>,
          <>
            <strong>Klicke auf eine Kachel</strong>, um zu sehen, welche deiner Kampagnen das
            Problem verursachen. So findest du heraus, ob es eine einzelne Kampagne ist oder ein
            allgemeines Problem mit Domain/Anbieter.
          </>,
          <>
            Vergleiche die <strong>Provider</strong>-Sektion (Gmail, Outlook, ...) mit der{" "}
            <strong>Sender-Domain</strong>-Sektion: liegt das Problem an einem Anbieter oder an
            einer deiner Versand-Domains?
          </>,
        ]}
        tips={[
          <>
            Ziel-Marke: <strong>Inbox-Rate über 90 %</strong>. Alles unter 70 % solltest du dir
            sofort ansehen.
          </>,
          <>
            Eine Zahl wie <code>12n</code> bedeutet: nur 12 Stichproben - das ist statistisch
            wackelig. Warte auf mehr Daten oder erhöhe die Seed-Frequenz, bevor du daraus Schlüsse
            ziehst.
          </>,
          <>
            Spam-Spitzen direkt nach DNS- oder Template-Änderungen sind typische Warnzeichen -
            zurückrollen und neu messen.
          </>,
          <>
            Die Daten aktualisieren sich automatisch alle 15 Sekunden (siehe "live"-Anzeige), du
            musst die Seite nicht neu laden.
          </>,
        ]}
        glossary={[
          {
            term: "Inbox-Rate",
            def: "Anteil der Test-Mails, die im normalen Posteingang gelandet sind. Höher = besser.",
          },
          {
            term: "Spam-Rate",
            def: 'Anteil der Mails im Spam-Ordner und im Tab "Promotions". Niedriger = besser.',
          },
          {
            term: "Provider",
            def: "Der E-Mail-Anbieter des Empfängers, z. B. Gmail, Outlook, GMX, Yahoo.",
          },
          {
            term: "Sender-Domain",
            def: "Die Domain, von der aus du sendest (z. B. notify.example.com).",
          },
          {
            term: "Seed-Postfach",
            def: "Ein echter Test-Account, an den jede Kampagne automatisch mitgesendet wird, damit wir sehen, wo sie landet.",
          },
          {
            term: "Stichprobe (n)",
            def: "Anzahl der ausgewerteten Test-Mails für diese Kachel. Mehr Stichproben = verlässlichere Aussage.",
          },
        ]}
      />

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={windowKey} onValueChange={(v) => setWindowKey(v as keyof typeof WINDOWS)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">Letzte Stunde</SelectItem>
            <SelectItem value="24h">Letzte 24 h</SelectItem>
            <SelectItem value="7d">Letzte 7 Tage</SelectItem>
          </SelectContent>
        </Select>
        <span className="inline-flex items-center gap-1 text-xs text-success">
          <span className="size-1.5 rounded-full bg-success animate-pulse" /> live
          <InfoTip>
            Die Seite aktualisiert sich automatisch alle 15 Sekunden und reagiert sofort auf neue
            Scan-Ergebnisse.
          </InfoTip>
        </span>
        <div className="ml-auto flex gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">
            {totals.total} Stichproben
            <InfoTip>Gesamtanzahl ausgewerteter Test-Mails im gewählten Zeitfenster.</InfoTip>
          </Badge>
          <Badge variant="outline" className="text-success">
            {totals.inbox} Inbox
          </Badge>
          <Badge variant="outline" className="text-destructive">
            {totals.spam} Spam
          </Badge>
          <Badge variant="outline" className="text-warning">
            {totals.promotions} Promotions
          </Badge>
        </div>
      </div>

      <Card className="border-border bg-card/60 p-4">
        <div className="mb-3">
          <h2 className="text-sm font-semibold">Status-Legende</h2>
          <p className="text-xs text-muted-foreground">
            Die Zahl zeigt Treffer im gewählten Zeitraum. Hover oder antippen erklärt das Signal.
          </p>
        </div>
        <PlacementStatusLegend counts={signalCounts} />
      </Card>

      <HeatmapSection
        title="Provider"
        icon={<Inbox className="size-4" />}
        cells={providerCells}
        onSelect={(label) => setDrill({ dim: "provider", label })}
      />

      <HeatmapSection
        title="Sender-Domain"
        icon={<Flame className="size-4" />}
        cells={domainCells}
        onSelect={(label) => setDrill({ dim: "sending_domain", label })}
      />

      {isLoading && <p className="text-sm text-muted-foreground">Lade Placement-Daten...</p>}
      {!isLoading && rows.length === 0 && (
        <Card className="p-6 bg-card/60 border-border">
          <p className="text-sm text-muted-foreground">
            Keine Placement-Ergebnisse im gewählten Zeitfenster. Sobald die Seed-Inbox-Scans
            Ergebnisse liefern, erscheint die Heatmap automatisch.
          </p>
        </Card>
      )}

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl">
          {drill && (
            <DrillDown
              dim={drill.dim}
              label={drill.label}
              rows={rows.filter((r) => (r[drill.dim] ?? "unknown") === drill.label)}
              campaignMap={data?.campaignMap ?? new Map()}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HeatmapSection({
  title,
  icon,
  cells,
  onSelect,
}: {
  title: string;
  icon: React.ReactNode;
  cells: Cell[];
  onSelect: (label: string) => void;
}) {
  return (
    <Card className="p-6 bg-card/60 border-border">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            {icon} {title}
          </h3>
          <p className="text-xs text-muted-foreground">
            Klick auf eine Kachel öffnet die Detail-Ansicht nach Kampagne. <strong>Farbe</strong> =
            Inbox-Rate (grün = gut, rot = schlecht). <strong>Balken unten</strong> zeigt die
            Verteilung: Inbox · Promotions · Spam.
          </p>
        </div>
      </div>
      {cells.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Daten.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
          {cells.map((c) => (
            <button
              key={c.key}
              onClick={() => onSelect(c.key)}
              className="text-left rounded-lg border border-border/60 p-3 hover:scale-[1.02] hover:border-primary/60 transition"
              style={{ background: rateColor(c.inboxRate, c.total) }}
            >
              <div className="text-xs font-medium truncate text-foreground/90" title={c.label}>
                {c.label}
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
                {pct(c.inboxRate)}
              </div>
              <div className="mt-1 flex items-center justify-between text-[10px] text-foreground/80">
                <span className="inline-flex items-center gap-1">
                  <AlertTriangle className="size-3" /> {pct(c.spamRate)}
                </span>
                <span>{c.total}n</span>
              </div>
              <div className="mt-2 flex h-1.5 rounded-full overflow-hidden bg-black/30">
                {c.total > 0 && (
                  <>
                    <div
                      className="bg-success"
                      style={{ width: `${(c.inbox / c.total) * 100}%` }}
                    />
                    <div
                      className="bg-warning"
                      style={{ width: `${(c.promotions / c.total) * 100}%` }}
                    />
                    <div
                      className="bg-destructive"
                      style={{ width: `${(c.spam / c.total) * 100}%` }}
                    />
                    <div className="bg-muted" style={{ width: `${(c.other / c.total) * 100}%` }} />
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function DrillDown({
  dim,
  label,
  rows,
  campaignMap,
}: {
  dim: "provider" | "sending_domain";
  label: string;
  rows: Row[];
  campaignMap: Map<string, string>;
}) {
  const byCampaign = useMemo(() => {
    const m = new Map<string, Cell>();
    for (const r of rows) {
      const cid = r.campaign_id ?? "-";
      let c = m.get(cid);
      if (!c) {
        c = {
          key: cid,
          label: campaignMap.get(cid) ?? cid.slice(0, 8),
          total: 0,
          inbox: 0,
          spam: 0,
          promotions: 0,
          other: 0,
          inboxRate: 0,
          spamRate: 0,
        };
        m.set(cid, c);
      }
      c.total++;
      const p = (r.placement || "").toLowerCase();
      if (p === "inbox") c.inbox++;
      else if (p === "spam") c.spam++;
      else if (p === "promotions") c.promotions++;
      else c.other++;
    }
    for (const c of m.values()) {
      c.inboxRate = c.total ? c.inbox / c.total : 0;
      c.spamRate = c.total ? (c.spam + c.promotions) / c.total : 0;
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [rows, campaignMap]);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Tag className="size-4" /> {dim === "provider" ? "Provider" : "Domain"}: {label}
        </DialogTitle>
        <DialogDescription>
          {rows.length} Samples · Kampagnen-Breakdown im gewählten Zeitfenster
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left py-2 px-2">Kampagne</th>
              <th className="text-right py-2 px-2">Inbox</th>
              <th className="text-right py-2 px-2">Spam</th>
              <th className="text-right py-2 px-2">Promo</th>
              <th className="text-right py-2 px-2">Inbox-Rate</th>
              <th className="text-right py-2 px-2">n</th>
            </tr>
          </thead>
          <tbody>
            {byCampaign.map((c) => (
              <tr key={c.key} className="border-b border-border/40">
                <td className="py-2 px-2 truncate max-w-[220px]" title={c.label}>
                  {c.label}
                </td>
                <td className="py-2 px-2 text-right text-success">{c.inbox}</td>
                <td className="py-2 px-2 text-right text-destructive">{c.spam}</td>
                <td className="py-2 px-2 text-right text-warning">{c.promotions}</td>
                <td className="py-2 px-2 text-right font-medium">{pct(c.inboxRate)}</td>
                <td className="py-2 px-2 text-right text-muted-foreground">{c.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
